import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { VariantGridDataService } from '@app/@core/services/variant-grid-data/variant-grid-data.service';
import { IDropDownOption } from '@app/add-product/models/multiselect-model';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'sp-multiselect-dropdown-chips',
    templateUrl: './multiselect-dropdown-chips.component.html',
    styleUrls: ['./multiselect-dropdown-chips.component.scss'],
})
export class MultiselectDropdownChipsComponent implements OnInit {
    @Input() options: Array<IDropDownOption> = [];
    @Input() optionPlaceHolder: string = '';
    @Input() isColorType: boolean = false;
    @Output() variantsDeletedEvent = new EventEmitter<boolean>();
    @Output() dropdownClosed = new EventEmitter<boolean>();
    selectedOptions: string[] = [];
    updateSelectedDropDownOptions: string[] = [];
    filteredOptions: Array<IDropDownOption>;
    selectedOptionsObservable: Observable<Array<IDropDownOption>>;
    selectedOptionsSubscription: Subscription;
    showNoResultText = false;
    searchInput = '';
    @ViewChild('optionsContainer') optionsContainer: ElementRef;

    constructor(private variantGridDataService: VariantGridDataService) {}

    ngOnInit(): void {
        this.filteredOptions = this.options;
        this.optionPlaceHolder =
            'Select ' +
            this.optionPlaceHolder
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase()) +
            ' Options';
        this.selectedOptionsObservable = this.isColorType
            ? this.variantGridDataService.getSelectedColorsObservable()
            : this.variantGridDataService.getSelectedSizesObservable();
        this.selectedOptionsSubscription =
            this.selectedOptionsObservable.subscribe(
                (selectedOptions: Array<IDropDownOption>) => {
                    this.selectedOptions = selectedOptions.map(
                        (option) => option.value
                    );
                }
            );
    }

    private getDropDownOptions(
        selectedOptions: string[]
    ): Array<IDropDownOption> {
        return selectedOptions.map((option: string) =>
            this.options.find(
                (optionObj: IDropDownOption) => optionObj.value === option
            )
        );
    }

    private findValueByLabel(value: string): string {
        for (const obj of this.options) {
            if (obj.label === value) {
                return obj.value;
            }
        }
        return undefined;
    }

    removeOption(option: string): void {
        const index = this.selectedOptions.indexOf(option);
        const variantGridDataLength: number =
            this.variantGridDataService.getVariantData().length;
        if (index >= 0) {
            this.variantGridDataService.removeSelectedOption(
                index,
                this.isColorType
            );
            if (variantGridDataLength > 0) this.variantsDeletedEvent.emit(true);
        }
    }

    getOptionLabel(optionValue: string): string {
        const option = this.options.find((c) =>
             c.value === optionValue || c.label === optionValue
    );
        return option ? option.label : '';
    }

    onDropdownFocus(): void {
        this.updateSelectedDropDownOptions = this.selectedOptions;
    }

    onDropdownBlur(): void {
        const selectedDropDownOptions: Array<IDropDownOption> =
            this.getDropDownOptions(this.selectedOptions);
        this.updateOptions(selectedDropDownOptions);
        this.updateSelectedDropDownOptions = [];
        this.filteredOptions = this.options;
        this.searchInput = '';
        this.showNoResultText = false;
        this.dropdownClosed.emit(true);
    }

    applyFilter(inputEvent: Event): void {
        const inputValue: string = (inputEvent.target as HTMLInputElement)
            .value;

        this.filteredOptions = this.options.filter((option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
        );

        if (!this.filteredOptions.length) {
            this.showNoResultText = true;
        } else {
            this.showNoResultText = false;
        }
    }

    updateOptions(selectedDropDownOptions: Array<IDropDownOption>): void {
        if (this.isColorType)
            this.variantGridDataService.intializeSelectedColors(
                selectedDropDownOptions
            );
        else {
            this.variantGridDataService.intializeSelectedSizes(
                selectedDropDownOptions
            );
        }
    }
    selectFilteredOption(option: IDropDownOption): void {
        if (this.selectedOptions.includes(option.value)) {
            this.selectedOptions = this.selectedOptions.filter(
                (val) => val !== option.value
            );
        } else {
            this.selectedOptions.push(option.value);
        }
    }
    onEnterKeyPressed(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            const focusedSelect = document.activeElement as HTMLElement;
            const activeDescendant = focusedSelect.getAttribute(
                'aria-activedescendant'
            );
            if (activeDescendant) {
                const focusedOption = document.getElementById(activeDescendant);
                if (
                    focusedOption &&
                    focusedOption.tagName.toLowerCase() === 'mat-option'
                ) {
                    const selectedOptionText = focusedOption
                        .getElementsByClassName('mat-option-text')[0]
                        .textContent.replace('done ', '')
                        .trim();
                    const value = this.findValueByLabel(selectedOptionText);
                    if (value) {
                        this.selectOptionThroughKeyBoard(value);
                    }
                }
            }
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); // Prevent default scrolling behavior

            const container = this.optionsContainer.nativeElement;
            const delta = event.key === 'ArrowDown' ? 42 : -42; // Adjust scroll speed

            container.scrollTop += delta; // Scroll the container
        }
    }

    selectOptionThroughKeyBoard(value: string): void {
        // Add or remove the selected option based on current selection
        const index = this.selectedOptions.indexOf(value);
        if (index !== -1) {
            this.selectedOptions.splice(index, 1);
        } else {
            this.selectedOptions.push(value);
        }
    }

    ngOnDestroy(): void {
        this.selectedOptionsSubscription.unsubscribe();
    }
}
